import type { CSSProperties, ReactNode } from "react";
import styled, { css } from "styled-components";

import { VideoSurface } from "../Video";
import { mediaViewportFitCss } from "../foundations/mediaViewportFit";
import type { AnnotationRasterGeometry } from "./annotationRasterStage";

/**
 * 스테이지 비율 사각형을 가장 가까운 size container 안에 양축으로 맞춘다. 비율은
 * `--c6-annotation-aspect-ratio`가 소유하고, 아직 모르면 `data-annotation-stage-fit="fill"`로
 * container를 채운다 — raster는 어느 쪽이든 `object-fit: contain`으로 같은 자리에 그려진다.
 */
export const annotationStageFitCss = css`
  ${mediaViewportFitCss({
    aspectRatio: "var(--c6-annotation-aspect-ratio)",
    maxHeight: "100cqh",
  })}
  height: auto;

  &[data-annotation-stage-fit="fill"] {
    width: 100%;
    height: 100%;
    aspect-ratio: auto;
  }
`;

/** stage geometry를 viewport의 비율 변수·fit 모드·관측용 attribute로 옮긴다. */
export function annotationStageProps({
  aspectRatio,
  geometry,
}: {
  readonly aspectRatio: number | null;
  readonly geometry: AnnotationRasterGeometry;
}) {
  return {
    "data-annotation-stage-fit": aspectRatio === null ? "fill" : "ratio",
    "data-annotation-raster-geometry": geometry,
    style: (aspectRatio === null
      ? {}
      : { "--c6-annotation-aspect-ratio": String(aspectRatio) }) as CSSProperties,
  };
}

export const AnnotationViewport = styled.div`
  position: relative;
  ${annotationStageFitCss}
  overflow: hidden;
  border-radius: ${({ theme }) => theme.shape.radius.surface};
  background: ${({ theme }) => theme.color.bg.surface};
  cursor: var(--c6-annotation-cursor, crosshair);

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.border.focus};
    outline-offset: 2px;
  }

  &[data-annotation-pointer-focus]:focus-visible {
    outline: none;
  }
`;

export const Raster = styled.div`
  position: absolute;
  inset: 0;
  transform-origin: 0 0;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    user-select: none;
    pointer-events: none;
  }
`;

export const EmptyRaster = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.color.bg.surface};
`;

/** 영상 프레임은 스테이지 사각형을 그대로 채운다 — 비율은 스테이지가 소유한다. */
export const CapturedVideoSurface = styled(VideoSurface)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;

  & > div {
    height: 100%;
    border: 0;
  }
`;

const OverlayLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;

  &[hidden] {
    display: none;
  }
`;

/**
 * raster 위 overlay 묶음. 판정은 `isAnnotationOverlayPresentable` 하나가 소유하고, 숨긴 동안에도
 * layer는 마운트된 채 데이터 조회·prefetch를 이어 간다.
 */
export function AnnotationOverlay({
  visible,
  children,
}: {
  readonly visible: boolean;
  readonly children: ReactNode;
}) {
  return (
    <OverlayLayer hidden={!visible} data-annotation-overlay={visible ? "visible" : "pending"}>
      {children}
    </OverlayLayer>
  );
}

export const ViewportOverlayActions = styled.div`
  position: absolute;
  top: 0.55rem;
  right: 0.55rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  pointer-events: none;
`;
